<template>
  <v-dialog v-model="dialog" max-width="900px" scrollable>
    <v-card>
      <v-card-title class="primary--text">
        Inscritos - {{ tournament?.name }}
      </v-card-title>
      <v-divider></v-divider>

      <v-card-text class="pa-6">
        <div v-if="loading" class="text-center py-8">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
        </div>

        <div v-else-if="registrations.length === 0" class="text-center py-8">
          <v-icon large color="grey lighten-1" class="mb-3">mdi-account-outline</v-icon>
          <p class="grey--text">No hay inscritos en este torneo</p>
        </div>

        <v-data-table
          v-else
          :headers="headers"
          :items="registrations"
          hide-default-footer
          class="elevation-0"
        >
          <template #item.player_display="{ item }">
            <div>
              <div class="font-weight-bold">{{ item.player_display }}</div>
              <div class="text-caption grey--text">
                {{ getParticipantType(item) }}
              </div>
            </div>
          </template>

          <template #item.player_handicap="{ item }">
            <v-chip small color="primary" text-color="white">
              {{ item.player_handicap }}
            </v-chip>
          </template>

          <template #item.status="{ item }">
            <v-chip :color="getStatusColor(item.status)" small text-color="white">
              {{ getRegistrationStatusLabel(item.status) }}
            </v-chip>
          </template>

          <template #item.registered_at="{ item }">
            {{ formatDateTime(item.registered_at) }}
          </template>

          <template #item.actions="{ item }">
            <v-menu offset-y small>
              <template #activator="{ on, attrs }">
                <v-btn icon small v-bind="attrs" v-on="on">
                  <v-icon small>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon small>mdi-information</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title class="text-caption">
                    {{ formatDateTime(item.registered_at) }}
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>
        </v-data-table>

        <!-- Estadísticas -->
        <v-divider class="my-4"></v-divider>
        <v-row class="mt-4">
          <v-col cols="12" sm="3">
            <div class="text-center">
              <div class="text-h5 font-weight-bold primary--text">
                {{ registrations.length }}
              </div>
              <div class="text-caption grey--text">Total inscritos</div>
            </div>
          </v-col>
          <v-col cols="12" sm="3">
            <div class="text-center">
              <div class="text-h5 font-weight-bold success--text">
                {{ getCountByStatus(2) }}
              </div>
              <div class="text-caption grey--text">Confirmados</div>
            </div>
          </v-col>
          <v-col cols="12" sm="3">
            <div class="text-center">
              <div class="text-h5 font-weight-bold warning--text">
                {{ getCountByStatus(1) }}
              </div>
              <div class="text-caption grey--text">Pendientes</div>
            </div>
          </v-col>
          <v-col cols="12" sm="3">
            <div class="text-center">
              <div class="text-h5 font-weight-bold error--text">
                {{ getCountByStatus(3) + getCountByStatus(4) }}
              </div>
              <div class="text-caption grey--text">Cancelados/No asistió</div>
            </div>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider></v-divider>
      <v-card-actions>
        <v-btn color="primary" text @click="openRegisterDialog">
          <v-icon small left>mdi-plus</v-icon>
          Agregar Jugador
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn text @click="$emit('close')">Cerrar</v-btn>
      </v-card-actions>
    </v-card>

    <!-- Register Player Dialog -->
    <RegisterPlayerDialog
      v-if="showRegisterDialog"
      :tournament="tournament"
      :loading="registeringPlayer"
      @register="handleRegisterPlayer"
      @close="showRegisterDialog = false"
    />
  </v-dialog>
</template>

<script>
import RegisterPlayerDialog from './RegisterPlayerDialog.vue'

export default {
  name: 'RegistrationsDialog',
  components: {
    RegisterPlayerDialog
  },
  props: {
    tournament: {
      type: Object,
      required: true
    },
    registrations: {
      type: Array,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      dialog: true,
      showRegisterDialog: false,
      registeringPlayer: false,
      headers: [
        { text: 'Jugador', value: 'player_display', sortable: false },
        { text: 'Handicap', value: 'player_handicap', sortable: false, width: '100px' },
        { text: 'Estado', value: 'status', sortable: false, width: '120px' },
        { text: 'Inscripcion', value: 'registered_at', sortable: false },
        { text: '', value: 'actions', sortable: false, width: '60px' }
      ]
    }
  },
  methods: {
    getParticipantType (registration) {
      if (registration.user) return 'Usuario'
      if (registration.ccslp_user) return 'CCSLP'
      if (registration.guest_name) return 'Invitado'
      return 'Desconocido'
    },
    getRegistrationStatusLabel (status) {
      const labels = {
        1: 'Pendiente',
        2: 'Confirmado',
        3: 'Cancelado',
        4: 'No asistió'
      }
      return labels[status] || 'Desconocido'
    },
    getStatusColor (status) {
      const colors = {
        1: 'warning',
        2: 'success',
        3: 'grey',
        4: 'error'
      }
      return colors[status] || 'grey'
    },
    formatDateTime (date) {
      return new Date(date).toLocaleDateString('es-ES', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    getCountByStatus (status) {
      return (this.registrations || []).filter(r => r.status === status).length
    },
    openRegisterDialog () {
      this.showRegisterDialog = true
    },
    async handleRegisterPlayer (data) {
      this.registeringPlayer = true
      try {
        this.$emit('register-player', {
          tournamentId: this.tournament.id,
          data
        })
        this.showRegisterDialog = false
      } catch (error) {
        console.error('Error registering player:', error)
      } finally {
        this.registeringPlayer = false
      }
    }
  }
}
</script>

<style scoped>
::v-deep .v-dialog__container {
  align-items: flex-start;
  padding-top: 20px;
}
</style>
